import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useCurrentRange } from "../lib/dataClient.js";
import { CONTRAST } from "../lib/gridMetrics.js";
import DotGrid from "./DotGrid.jsx";
import GridStatus, { RevalidatingOverlay } from "./GridStatus.jsx";
import Select from "./Select.jsx";
import { isDimensionVisible } from "../lib/dimensionDefs.js";

const {
  LABEL_W, HEADER_H, TOOLBAR_H, PAD_R, PAD_B,
  COL_MIN: MIN_COL_W, COL_MAX: MAX_COL_W,
  ROW_MIN: MIN_ROW_H, ROW_MAX: MAX_ROW_H,
  MAX_ROWS, MAX_COLS,
  DOT_MIN_R: MIN_DOT_RADIUS, DOT_MAX_R: MAX_DOT_RADIUS,
} = CONTRAST;

const BG_ALPHA_MAX = 0.48;

const DIM_OPTIONS = [
  { key: "endpoints", label: "endpoints" },
  { key: "identities", label: "identities" },
  { key: "clusters", label: "topics" },
  { key: "sessions", label: "traces" },
  { key: "agents", label: "agents" },
  { key: "tools", label: "tools" },
  { key: "files", label: "files" },
  { key: "commands", label: "commands" },
  { key: "hosts", label: "hosts" },
  { key: "urls", label: "urls" },
  { key: "skills", label: "skills" },
];

function ContrastGrid({
  rows, cols,
  matrix,
  expectedMatrix, surpriseMatrix,
  maxExpected, minExpected, maxSurprise,
  selfRowIndex, selfColIndex,
  hoveredRowIndex, hoveredColIndex,
  cellW, rowH,
  onHoverChange,
  onNavigate,
}) {
  const expectedFloor = minExpected || 0;
  const expectedRange = Math.max(1e-6, (maxExpected || 1) - expectedFloor);
  const surpriseMax = maxSurprise || 1;
  const maxRadius = Math.min(MAX_DOT_RADIUS, Math.min(cellW, rowH) * 0.46);
  const minRadius = Math.min(maxRadius, Math.max(MIN_DOT_RADIUS, maxRadius * 0.38));

  const cellFn = useCallback((ri, ci) => {
    const surprise = surpriseMatrix[ri]?.[ci] || 0;
    const expected = expectedMatrix[ri]?.[ci] || 0;
    let shade = null;
    if (expected > 0) {
      const norm = Math.max(0, (expected - expectedFloor) / expectedRange);
      shade = { opacity: Math.sqrt(norm) * BG_ALPHA_MAX };
    }
    if (surprise > 0) {
      const norm = Math.sqrt(surprise / surpriseMax);
      const r = minRadius + norm * (maxRadius - minRadius);
      const opacity = Math.min(1, 0.62 + norm * 0.38);
      return { shade, dot: { r, opacity } };
    }
    return { shade, placeholder: { r: minRadius * 0.6, opacity: 0.16 } };
  }, [surpriseMatrix, expectedMatrix, expectedFloor, expectedRange, surpriseMax, minRadius, maxRadius]);

  // Cell tooltip — explains the cell in human terms instead of math
  // notation. The headline is the lift descriptor; supporting lines spell
  // out the numbers and what the baseline means so the reader doesn't
  // have to guess what "expected" is referring to.
  const cellTooltipFn = useCallback((ri, ci, row, col) => {
    if (!row || !col) return null;
    const observed = matrix[ri]?.[ci] || 0;
    const expected = expectedMatrix[ri]?.[ci] || 0;
    if (observed === 0 && expected === 0) return null;
    const lift = expected > 0 ? observed / expected : 0;
    const rowLabel = row.name || row.label || row.id;
    const colLabel = col.name || col.label || col.id;
    const phrase = observed > 0 && expected > 0 ? liftPhrase(lift) : null;
    return (
      <div className="flex flex-col gap-[4px]">
        <div className="flex flex-col gap-[1px] font-medium text-[color:var(--ink)]">
          <div className="flex min-w-0 items-baseline gap-[6px]">
            <span className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">row</span>
            <span className="min-w-0 truncate">{rowLabel}</span>
          </div>
          <div className="flex min-w-0 items-baseline gap-[6px]">
            <span className="shrink-0 text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">col</span>
            <span className="min-w-0 truncate">{colLabel}</span>
          </div>
        </div>
        {observed > 0 && expected > 0 ? (
          <>
            <div className={lift >= 1.5 ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}>
              {phrase ? `${phrase} · ` : ""}{lift.toFixed(1)}× the baseline rate
            </div>
            <div className="text-[color:var(--muted)]">
              {observed} traces here, ~{formatNum(expected)} baseline
            </div>
            <div className="text-[10px] text-[color:var(--muted-soft)] leading-[1.3]">
              Baseline = this row's total traces × this column's share of all activity.
              It's the count we'd see if the row didn't favor any column.
            </div>
          </>
        ) : observed > 0 ? (
          <div className="text-[color:var(--muted)]">
            {observed} traces here · no baseline available for this cell
          </div>
        ) : (
          <div className="text-[color:var(--muted)]">
            No traces here · baseline would be ~{formatNum(expected)}
          </div>
        )}
      </div>
    );
  }, [matrix, expectedMatrix]);

  const handleHover = useCallback((hit) => {
    if (!hit) { onHoverChange(null); return; }
    if (hit.type === "row") onHoverChange({ kind: "row", ri: hit.ri });
    else if (hit.type === "col") onHoverChange({ kind: "col", ci: hit.ci });
    else onHoverChange({ kind: "cell", ri: hit.ri, ci: hit.ci });
  }, [onHoverChange]);

  return (
    // Inset the grid from the panel's outer border so row labels and the
    // top of the column headers aren't pressed against the line. The
    // panel's own border draws around this padded area.
    <div className="h-full w-full pl-[10px] pr-[6px] pt-[8px] pb-[6px]">
      <DotGrid
        rows={rows}
        cols={cols}
        cellW={cellW}
        rowH={rowH}
        labelW={LABEL_W}
        headerH={HEADER_H}
        colHeaderLines={2}
        rowLabelAlign="right"
        cellFn={cellFn}
        cellTooltipFn={cellTooltipFn}
        selfRowIndex={selfRowIndex}
        selfColIndex={selfColIndex}
        hoveredRowIndex={hoveredRowIndex}
        hoveredColIndex={hoveredColIndex}
        onHover={handleHover}
        onClick={onNavigate}
      />
    </div>
  );
}

function formatNum(v) {
  if (v >= 100) return String(Math.round(v));
  if (v >= 10) return v.toFixed(1).replace(/\.0$/, "");
  return v.toFixed(1);
}

// Verbal qualifier for a lift ratio — gives the hover strip something a
// human can scan without doing arithmetic. The numeric lift is shown
// alongside so the precise value is still available.
function liftPhrase(lift) {
  if (!isFinite(lift) || lift <= 0) return null;
  if (lift >= 3) return "much more than expected";
  if (lift >= 1.5) return "above baseline";
  if (lift >= 0.8) return "about baseline";
  if (lift >= 0.3) return "below baseline";
  return "much less than expected";
}

// Two-row hover strip — left half shows whatever the user is pointing at
// (row activity / col activity / cell readout) in plain English; right
// half is reserved for the static legend so the user always sees the
// shade/dot decoder. When a cell is hovered, the right half collapses
// into a compact lift summary so the strip never wraps.
function HoverStrip({ hover, rows, cols, matrix, expectedMatrix }) {
  const legend = (
    <span className="inline-flex items-center gap-[10px] normal-case tracking-normal text-[color:var(--muted-soft)]">
      <span className="inline-flex items-center gap-[4px]">
        <span className="h-[8px] w-[8px] rounded-[1px] bg-[color:var(--green)]" style={{ opacity: 0.35 }} />
        baseline rate
      </span>
      <span className="inline-flex items-center gap-[4px]">
        <span className="h-[7px] w-[7px] rounded-full bg-[color:var(--green)]" />
        above baseline
      </span>
    </span>
  );
  if (!hover) {
    return (
      <span className="flex items-center justify-between gap-[12px] w-full normal-case tracking-normal">
        <span className="text-[color:var(--muted-soft)]">hover a cell to inspect</span>
        {legend}
      </span>
    );
  }

  if (hover.kind === "row") {
    const r = rows[hover.ri];
    if (!r) return null;
    return (
      <span className="flex items-center justify-between gap-[12px] w-full normal-case tracking-normal">
        <span className="min-w-0 truncate text-[color:var(--ink)]">
          <span className="text-[color:var(--muted-soft)]">row</span> {r.name}
          <span className="text-[color:var(--muted-soft)]"> · </span>{r.activity} traces total
        </span>
        {legend}
      </span>
    );
  }

  if (hover.kind === "col") {
    const c = cols[hover.ci];
    if (!c) return null;
    return (
      <span className="flex items-center justify-between gap-[12px] w-full normal-case tracking-normal">
        <span className="min-w-0 truncate text-[color:var(--ink)]">
          <span className="text-[color:var(--muted-soft)]">col</span> {c.name}
          <span className="text-[color:var(--muted-soft)]"> · </span>{c.activity} traces total
        </span>
        {legend}
      </span>
    );
  }

  const r = rows[hover.ri];
  const c = cols[hover.ci];
  if (!r || !c) return null;
  const observed = matrix[hover.ri]?.[hover.ci] || 0;
  const expected = expectedMatrix[hover.ri]?.[hover.ci] || 0;
  const lift = expected > 0 ? observed / expected : 0;
  const phrase = observed > 0 && expected > 0 ? liftPhrase(lift) : null;
  return (
    <span className="flex items-center justify-between gap-[12px] w-full normal-case tracking-normal">
      <span className="min-w-0 truncate text-[color:var(--ink)]">
        {r.name} <span className="text-[color:var(--muted-soft)]">×</span> {c.name}
        <span className="text-[color:var(--muted-soft)]"> · </span>
        {observed} traces
        {observed > 0 && expected > 0 ? (
          <>
            <span className="text-[color:var(--muted-soft)]"> · </span>
            <span className={lift >= 1.5 ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}>
              {lift.toFixed(1)}× baseline of ~{formatNum(expected)}
            </span>
          </>
        ) : observed > 0 ? (
          <>
            <span className="text-[color:var(--muted-soft)]"> · </span>
            <span className="text-[color:var(--muted)]">no baseline for this cell</span>
          </>
        ) : null}
      </span>
      {phrase ? (
        <span className="shrink-0 text-[color:var(--muted-soft)]">{phrase}</span>
      ) : (
        legend
      )}
    </span>
  );
}

function DimSelect({ value, options, onChange }) {
  return (
    <Select
      value={value}
      onChange={onChange}
      options={options.map((d) => ({ value: d.key, label: d.label }))}
      variant="inline"
      ariaLabel="Dimension"
    />
  );
}

export default function ContrastPanel({ scopePath = [], onNavigate, onNavigatePath }) {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hover, setHover] = useState(null);
  const [rowDimOverride, setRowDimOverride] = useState(null);
  const [colDimOverride, setColDimOverride] = useState(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setSize((prev) => {
        const next = { width: Math.round(rect.width), height: Math.round(rect.height) };
        if (prev.width === next.width && prev.height === next.height) return prev;
        return next;
      });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Axes are locked to endpoints × clusters. Navigation never rotates them —
  // clicks filter the dataset (parentScope) and surface self/sibling peers on
  // whichever axis was clicked. Axes only change when the user picks a
  // different dim via the DimSelect dropdowns; those overrides are sticky.
  const rowDim = rowDimOverride || "endpoints";
  const defaultColDim = rowDim === "clusters" ? "endpoints" : "clusters";
  const colDim = colDimOverride && colDimOverride !== rowDim
    ? colDimOverride
    : defaultColDim;

  // Strip any scope steps that sit on the row or col axis from the filter
  // passed to the worker. Keeping them would collapse the matrix to a single
  // cell (the pinned value filters out every peer). Instead, we let the
  // worker score freely across the parent-sibling pool and highlight the
  // pinned values via selfId / selfColId.
  // Use the DEEPEST step on each axis (the current drill level), not the
  // first. A hierarchical cluster axis can carry several cluster steps in the
  // path (root > … > leaf); contrast must pin/drill the level the user is
  // actually on, not the top-level ancestor `.find` would return.
  const rowSelfStep = useMemo(
    () => [...scopePath].reverse().find((s) => s.dim === rowDim) || null,
    [scopePath, rowDim]
  );
  const colSelfStep = useMemo(
    () => [...scopePath].reverse().find((s) => s.dim === colDim) || null,
    [scopePath, colDim]
  );
  const selfId = rowSelfStep?.id || null;
  const selfColId = colSelfStep?.id || null;
  const parentScope = useMemo(
    () => scopePath.filter((s) => s.dim !== rowDim && s.dim !== colDim),
    [scopePath, rowDim, colDim]
  );

  // Subtract the inner grid wrapper's CSS padding (pl-[10px] pr-[6px]
  // pt-[8px] pb-[6px] in ContrastGrid) so the cell-count math doesn't
  // overestimate the available area. Without this the grid still renders
  // because DotGrid clamps cellW internally, but cell counts can be
  // slightly off near the edges.
  const GRID_INSET_X = 10 + 6;
  const GRID_INSET_Y = 8 + 6;
  const availableW = Math.max(0, (size.width || 0) - LABEL_W - PAD_R - GRID_INSET_X);
  const availableH = Math.max(0, (size.height || 0) - HEADER_H - PAD_B - TOOLBAR_H - GRID_INSET_Y);

  // Pick cell counts so rendered cell sizes land inside [MIN, MAX]. Using the
  // MIN as divisor here gives the *maximum* count that still meets the
  // legibility floor — i.e. densely packed. rowH/cellW below then clamps back
  // into [MIN, MAX] so padding fills whatever slack is left.
  //
  // Previously we snapped to multiples of 2/3 for "even" alignment; the
  // result was visible chunky jumps when the panel was resized (row count
  // would step from 6→9→12 as height crossed thresholds, and rowH would
  // pop accordingly). Letting the raw count through means height scales
  // smoothly with the container — rows grow and shrink one at a time.
  const rawColCount = availableW > 0 ? Math.floor(availableW / MIN_COL_W) : 6;
  const rawRowCount = availableH > 0 ? Math.floor(availableH / MIN_ROW_H) : 12;
  const fittedCols = Math.min(MAX_COLS, Math.max(2, rawColCount));
  const fittedRows = Math.min(MAX_ROWS, Math.max(3, rawRowCount));

  const range = useCurrentRange();
  // Stale-while-revalidate cache so hovering a swimlane cell (which mounts
  // the CellCoOccurrencePanel in place of this one) and then hovering out
  // doesn't refetch the matrix every time. The cache key already covers
  // scope / range / dim / self, so a real scope or range change invalidates
  // it naturally. 5min ceiling is plenty for a single browsing session.
  const { data, loading } = useQuery(
    "getContrastMatrix",
    {
      scopePath: parentScope,
      rowDim,
      colDim,
      rowLimit: fittedRows,
      colLimit: fittedCols,
      selfId,
      selfColId,
      range,
    },
    { staleMs: 5 * 60 * 1000 },
  );

  // Server now picks rows/cols by TV-divergence × √activity (matching the
  // worker), so the previous "biased visible-totals" expected baseline is
  // gone. Nothing else changes shape on the consumer side.
  const rawRows = data?.rows || [];
  const rawCols = data?.cols || [];
  const matrix = data?.matrix || [];
  const liftMatrix = data?.liftMatrix || [];
  const expectedMatrix = data?.expectedMatrix || [];
  const surpriseMatrix = data?.surpriseMatrix || [];
  const maxExpected = data?.maxExpected || 0;
  const minExpected = data?.minExpected || 0;
  const maxSurprise = data?.maxSurprise || 0;
  const selfRowIndex = data?.selfRowIndex ?? -1;
  const selfColIndex = data?.selfColIndex ?? -1;

  // Pad rows/cols to the fitted floor so the grid always fills the panel.
  // Padding entries carry `isPadding: true` so navigation/hover ignore them
  // and no dots draw (matrix indices past real data return 0).
  const rows = useMemo(() => {
    if (rawRows.length >= fittedRows) return rawRows;
    const padded = [...rawRows];
    while (padded.length < fittedRows) {
      padded.push({ id: `__pad_r_${padded.length}`, name: "", isPadding: true });
    }
    return padded;
  }, [rawRows, fittedRows]);
  const cols = useMemo(() => {
    if (rawCols.length >= fittedCols) return rawCols;
    const padded = [...rawCols];
    while (padded.length < fittedCols) {
      padded.push({ id: `__pad_c_${padded.length}`, name: "", isPadding: true });
    }
    return padded;
  }, [rawCols, fittedCols]);

  // Clamp actual cell size to [MIN, MAX]. With padding bringing cols/rows up
  // to fittedCols/fittedRows, floor(available / length) lands at ~MIN.
  const cellW = cols.length > 0
    ? Math.max(MIN_COL_W, Math.min(MAX_COL_W, Math.floor(availableW / cols.length)))
    : MIN_COL_W;
  const rowH = rows.length > 0
    ? Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, Math.floor(availableH / rows.length)))
    : MIN_ROW_H;

  const hasAnyData = rawRows.length > 0 && rawCols.length > 0;

  const initialLoad = loading && !data;
  // Stale-while-revalidate transition: scope or range changed and we're
  // still showing the previous matrix. Dim+pulse overlay tells the user
  // a refresh is in flight without blanking the grid (which would
  // double-flash when the user is e.g. sweeping through a scope tree).
  const revalidating = loading && !!data;

  // Axes can be any two distinct dims. Pinned-in-scope dims are allowed —
  // they're filtered out of parentScope so the axis can still span peers.
  // Honor the same analytics visibility set as the sidebar registry so a
  // hidden dimension is not offered as a contrast axis.
  const visibleDimOptions = DIM_OPTIONS.filter((d) => isDimensionVisible(d.key));
  const rowDimOptions = visibleDimOptions.filter((d) => d.key !== colDim);
  const colDimOptions = visibleDimOptions.filter((d) => d.key !== rowDim);

  return (
    <div className="flex h-full min-h-0 flex-col pt-[6px] pb-[8px]">
      <div
        ref={containerRef}
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden border border-[color:var(--line)] bg-[color:var(--bg)]"
      >
        <div
          className="flex flex-col border-b border-[color:var(--line-soft)] text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)]"
          style={{ height: TOOLBAR_H }}
        >
          <div className="flex h-[21px] min-h-[21px] items-center gap-[8px] px-[11px]">
            <span className="shrink-0 text-[color:var(--muted-soft)]">rows:</span>
            <DimSelect
              value={rowDim}
              options={rowDimOptions}
              onChange={(v) => setRowDimOverride(v)}
            />
          </div>
          <div className="flex h-[21px] min-h-[21px] items-center gap-[8px] border-t border-[color:var(--line-soft)] px-[11px]">
            <span className="shrink-0 text-[color:var(--muted-soft)]">cols:</span>
            <DimSelect
              value={colDim}
              options={colDimOptions}
              onChange={(v) => setColDimOverride(v)}
            />
          </div>
          <div className="flex h-[22px] min-h-[22px] flex-1 items-center gap-[12px] overflow-hidden border-t border-[color:var(--line-soft)] px-[11px]">
            <span className="flex min-w-0 flex-1 items-center overflow-hidden">
              <HoverStrip
                hover={hover}
                rows={rows}
                cols={cols}
                matrix={matrix}
                expectedMatrix={expectedMatrix}
              />
            </span>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden">
          {initialLoad ? (
            <GridStatus state="loading" label="Computing contrast…" />
          ) : !hasAnyData ? (
            <GridStatus state="empty" label="No contrast signal. Widen the date range." />
          ) : (
            <ContrastGrid
              rows={rows}
              cols={cols}
              matrix={matrix}
              expectedMatrix={expectedMatrix}
              surpriseMatrix={surpriseMatrix}
              maxExpected={maxExpected}
              minExpected={minExpected}
              maxSurprise={maxSurprise}
              selfRowIndex={selfRowIndex}
              selfColIndex={selfColIndex}
              hoveredRowIndex={hover && (hover.kind === "row" || hover.kind === "cell") ? hover.ri : -1}
              hoveredColIndex={hover && (hover.kind === "col" || hover.kind === "cell") ? hover.ci : -1}
              cellW={cellW}
              rowH={rowH}
              onHoverChange={setHover}
              onNavigate={(hit) => {
                if (hit.type === "row") {
                  const nextPath = [
                    ...parentScope,
                    { dim: rowDim, id: hit.row.id },
                    ...(colSelfStep ? [colSelfStep] : []),
                  ];
                  if (onNavigatePath) onNavigatePath(nextPath);
                  else onNavigate(rowDim, hit.row.id);
                } else if (hit.type === "col") {
                  const nextPath = [
                    ...parentScope,
                    ...(rowSelfStep ? [rowSelfStep] : []),
                    { dim: colDim, id: hit.col.id },
                  ];
                  if (onNavigatePath) onNavigatePath(nextPath);
                  else onNavigate(colDim, hit.col.id);
                } else if (hit.type === "cell") {
                  const nextPath = [
                    ...parentScope,
                    { dim: rowDim, id: hit.row.id },
                    { dim: colDim, id: hit.col.id },
                  ];
                  if (onNavigatePath) onNavigatePath(nextPath);
                  else onNavigate(colDim, hit.col.id);
                }
              }}
            />
          )}
        </div>
        <RevalidatingOverlay active={revalidating} label="Refreshing contrast…" />
      </div>
    </div>
  );
}
